import { newRelic } from '~/utils/newRelic/newRelic';

import { SCAN_DETECTION_DEFAULTS } from '../constants/scanDetection.const';
import {
  type ScanDetector,
  type ScanDetectorOptions,
  type ScanDetectorTimer,
} from '../types/scanDetector.type';
// TEMPORARY: scan tuning probe, remove with `scanTuningProbe.util.ts`
import { createScanTuningProbe } from './scanTuningProbe.util';

interface BufferedCharacter {
  timestamp: number;
  value: string;
}

type ScanDetectionRejectionReason =
  | 'duplicate_within_window'
  | 'inter_key_slack_exceeded'
  | 'max_avg_time_exceeded'
  | 'max_duration_exceeded';

/** Standalone modifier keydowns that must not reset an in-progress wedge burst. */
const isIgnorableModifierKey = (key: string) =>
  key === 'Shift' ||
  key === 'CapsLock' ||
  key === 'NumLock' ||
  key === 'ScrollLock' ||
  key === 'AltGraph';

const logScanDetectionRejection = (
  reasons: ScanDetectionRejectionReason[],
  customAttributes: Record<string, unknown>,
) => {
  newRelic.log('Scan detection rejected candidate', {
    customAttributes: {
      reasons: reasons.join(','),
      ...customAttributes,
    },
    level: 'DEBUG',
  });
};

export const createScanDetector = ({
  now = Date.now,
  onScan,
  timers = window,
  ...overrides
}: ScanDetectorOptions): ScanDetector => {
  const settings = { ...SCAN_DETECTION_DEFAULTS, ...overrides };
  // TEMPORARY: scan tuning probe, remove with `scanTuningProbe.util.ts`
  const logTuningCandidate = createScanTuningProbe(settings);
  let bufferedCharacters: BufferedCharacter[] = [];
  let bufferTimer: ScanDetectorTimer | undefined;
  let isStarted = false;
  let lastDetected: { barcodeValue: string; timestamp: number } | undefined;

  const clearBufferTimer = () => {
    if (bufferTimer !== undefined) timers.clearTimeout(bufferTimer);
    bufferTimer = undefined;
  };

  const reset = () => {
    clearBufferTimer();
    bufferedCharacters = [];
  };

  const isSuffix = (event: KeyboardEvent) => settings.suffixDetection.includes(event.key);

  const finalize = (): boolean => {
    clearBufferTimer();

    const candidate = bufferedCharacters;
    bufferedCharacters = [];

    if (candidate.length < settings.minLength) return false;

    const barcodeValue = candidate.map(({ value }) => value).join('');
    const intervals = candidate
      .slice(1)
      .map((character, index) => character.timestamp - candidate[index].timestamp);
    const durationMs = candidate[candidate.length - 1].timestamp - candidate[0].timestamp;
    const meanIntervalMs =
      intervals.reduce((total, interval) => total + interval, 0) / intervals.length;
    const slowGapCount = intervals.filter((interval) => interval > settings.maxInterKeyMs).length;

    const timingRejectionReasons: ScanDetectionRejectionReason[] = [];
    if (meanIntervalMs > settings.maxAvgTimeMs)
      timingRejectionReasons.push('max_avg_time_exceeded');
    if (durationMs > settings.maxDurationMs) timingRejectionReasons.push('max_duration_exceeded');
    if (slowGapCount > settings.interKeySlackCount)
      timingRejectionReasons.push('inter_key_slack_exceeded');

    if (timingRejectionReasons.length > 0) {
      logScanDetectionRejection(timingRejectionReasons, {
        barcodeLength: barcodeValue.length,
        durationMs,
        interKeySlackCount: settings.interKeySlackCount,
        maxAvgTimeMs: settings.maxAvgTimeMs,
        maxDurationMs: settings.maxDurationMs,
        maxInterKeyMs: settings.maxInterKeyMs,
        meanIntervalMs,
        slowGapCount,
      });
      return false;
    }

    const detectedAt = now();

    if (
      lastDetected?.barcodeValue === barcodeValue &&
      detectedAt - lastDetected.timestamp < settings.duplicateWindowMs
    ) {
      logScanDetectionRejection(['duplicate_within_window'], {
        barcodeLength: barcodeValue.length,
        duplicateWindowMs: settings.duplicateWindowMs,
        msSinceLastDetection: detectedAt - lastDetected.timestamp,
      });

      // Recognised as a scan and deliberately dropped, so its suffix is still ours
      // to consume.
      return true;
    }

    lastDetected = { barcodeValue, timestamp: detectedAt };
    onScan(barcodeValue);

    return true;
  };

  // TEMPORARY: scan tuning probe, remove with `scanTuningProbe.util.ts`
  const finalizeAndLogTuning = (finalizedBy: 'suffix' | 'timeout'): boolean => {
    // `finalize` reassigns the buffer rather than emptying it, so this reference survives.
    const candidate = bufferedCharacters;
    const accepted = finalize();
    logTuningCandidate(candidate, finalizedBy);

    return accepted;
  };

  const armBufferTimer = () => {
    clearBufferTimer();
    bufferTimer = timers.setTimeout(
      () => finalizeAndLogTuning('timeout'),
      settings.bufferTimeoutMs,
    );
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.isComposing || event.repeat || event.ctrlKey || event.metaKey || event.altKey) {
      reset();
      return;
    }

    if (isSuffix(event)) {
      // TEMPORARY: `finalizeAndLogTuning` is the scan tuning probe wrapper around `finalize`
      if (finalizeAndLogTuning('suffix')) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    if (event.key.length !== 1) {
      // Keyboard wedges emit Shift (etc.) before uppercase/symbol chars; ignore those.
      if (isIgnorableModifierKey(event.key)) return;
      reset();
      return;
    }

    bufferedCharacters.push({ timestamp: event.timeStamp, value: event.key });
    armBufferTimer();
  };

  return {
    reset,
    start: () => {
      if (isStarted) return;
      window.addEventListener('keydown', onKeyDown, true);
      isStarted = true;
    },
    stop: () => {
      if (!isStarted) return;
      window.removeEventListener('keydown', onKeyDown, true);
      isStarted = false;
      reset();
    },
  };
};
